import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { COOKIE_CATEGORIES } from './cookieCategories';
import { CookieConsentContext } from './cookieConsentContext';
import { getCookieConsentService } from './CookieConsentService';
import type { CookieConsentPreferences, CookieConsentRecord } from './types';

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const service = getCookieConsentService();
  const [consent, setConsent] = useState<CookieConsentRecord | null>(() => service.getConsent());
  const [showPreferences, setShowPreferences] = useState(false);

  useEffect(() => {
    service.applyConsent();
    return service.subscribe(setConsent);
  }, [service]);

  const hasConsent = consent !== null;
  const showBanner = !hasConsent;

  const acceptAll = useCallback(() => {
    service.acceptAll();
    setShowPreferences(false);
  }, [service]);

  const acceptEssentialOnly = useCallback(() => {
    service.acceptEssentialOnly();
    setShowPreferences(false);
  }, [service]);

  const savePreferences = useCallback(
    (prefs: Partial<CookieConsentPreferences>) => {
      service.savePreferences(prefs);
      setShowPreferences(false);
    },
    [service],
  );

  const value = useMemo(
    () => ({
      consent,
      hasConsent,
      showBanner,
      showPreferences,
      categories: COOKIE_CATEGORIES,
      acceptAll,
      acceptEssentialOnly,
      openPreferences: () => setShowPreferences(true),
      closePreferences: () => setShowPreferences(false),
      savePreferences,
      isCategoryEnabled: (category: keyof CookieConsentPreferences) =>
        service.isCategoryEnabled(category),
    }),
    [
      consent,
      hasConsent,
      showBanner,
      showPreferences,
      acceptAll,
      acceptEssentialOnly,
      savePreferences,
      service,
    ],
  );

  return <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>;
}
